import {observer} from 'mobx-react';
import React from 'react';

import {
    AsyncPriceCourseCardContext,
    BrowseCourseBadgesContext,
    COURSE_BADGE_PREMIUM,
} from '@udemy/browse-course';
import {useMatchMedia} from '@udemy/hooks';
import {CertificationBundle} from '@udemy/react-certification-preparation';
import {useFeatures} from '@udemy/react-experimentation-provider';
import {useStores} from '@udemy/store-provider';
import {useConsumerSubscriptionPlan, ConsumerSubscriptionPlan} from '@udemy/subscription-graphql';

import {
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    MARKETPLACE_SUBSCRIPTION_OFFER,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
    UPDATE_PERSONAL_PLAN_TOPIC_PAGE,
    USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
    CERTIFICATION_BUNDLE_ON_TOPIC_CLP,
    CERTIFICATION_BUNDLE_EXPANSION,
    GENERIC_BUNDLES_ON_TOPICS,
    COURSE_GUIDANCE,
    QUICK_VIEW_BOX_FEATURE,
    MX_MOBILE_VERTICAL_RECO_UNITS,
    TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE,
    FBT_FORCE_GO_TO_CART,
    TOPIC_SPECIAL_BUNDLE_PRICING,
} from 'src/lib/experimentation-constants';
import {getBrowseCourseBadgesContextOptionsByExperiment} from 'src/lib/experimentation-helper';
import {RootStore} from 'src/stores/root-store';

import {TOPIC_PRESET_VALUES} from './constants';
import {FreeTopic} from './free-topic.react-component';
import {Topic, TopicPageExperimentVariants, TopicProps} from './topic.react-component';

export type TopicWrapperProps = Omit<TopicProps, 'udData'>;

export const TopicPageExperimentationFeatures = [
    UPDATE_PERSONAL_PLAN_TOPIC_PAGE,
    MARKETPLACE_SUBSCRIPTION_OFFER,
    USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL,
    SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE,
    DELAY_COURSE_CARD_QUICK_VIEW_BOX,
    HIDE_WISHLIST_BUTTON_ON_COURSE_CARD_QUICK_VIEW_BOX,
    PERSONAL_PLAN_COURSE_ENROLLMENT,
    CERTIFICATION_BUNDLE_ON_TOPIC_CLP,
    CERTIFICATION_BUNDLE_EXPANSION,
    GENERIC_BUNDLES_ON_TOPICS,
    COURSE_GUIDANCE,
    QUICK_VIEW_BOX_FEATURE,
    COURSE_GUIDANCE,
    MX_MOBILE_VERTICAL_RECO_UNITS,
    TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE,
    FBT_FORCE_GO_TO_CART,
    TOPIC_SPECIAL_BUNDLE_PRICING,
];

export const TopicWrapper: React.FC<TopicWrapperProps> = observer((props: TopicWrapperProps) => {
    const isMobileMax = useMatchMedia('mobile-max');
    const consumerSubscriptionPlan = useConsumerSubscriptionPlan();
    const {isLoading, featureVariantAssignments: features} = useFeatures(
        TopicPageExperimentationFeatures,
    );

    const threeCardVerticalLayoutExperimentIsEnabled =
        features.get(MX_MOBILE_VERTICAL_RECO_UNITS)?.configuration?.showVerticalLayout ?? false;
    const showThreeCardVerticalLayout = !!isMobileMax && threeCardVerticalLayoutExperimentIsEnabled;

    const experimentProps: TopicPageExperimentVariants = {
        isExperimentConfigsLoading: isLoading,
        showUpdatedPPTopicPage:
            features.get(UPDATE_PERSONAL_PLAN_TOPIC_PAGE)?.configuration?.showUpdatedPPTopicPage ===
            'on'
                ? true
                : false,
        showPersonalPlanBadge:
            features.get(MARKETPLACE_SUBSCRIPTION_OFFER)?.configuration?.show_personal_plan_badge ??
            false,
        // Experiment is archived. We will be removing this soon.
        showCodingExercisesBadge: false,
        useStandardizeMobileBrowseCarousel:
            features.get(USE_STANDARDIZE_MOBILE_BROWSE_CAROUSEL)?.configuration
                ?.useStandardizeBrowseCarousel ?? false,
        showCourseGuidance:
            features.get(COURSE_GUIDANCE)?.configuration?.show_course_guidance_feature ?? false,
        fullQuickViewBoxFeature:
            features.get(QUICK_VIEW_BOX_FEATURE)?.configuration?.feature ?? false,
        showThreeCardVerticalLayout: showThreeCardVerticalLayout,
        showAddToCartInThreeCardLayout:
            features.get(MX_MOBILE_VERTICAL_RECO_UNITS)?.configuration?.showAddToCartButtons ??
            false,
        isTopicVoucherModuleAboveBundle: features.get(TOPIC_VOUCHER_MODULE_ABOVE_BUNDLE),
        showTopicSpecialBundlePricing: features.get(TOPIC_SPECIAL_BUNDLE_PRICING),
    };

    const subscriptionCatalogDifferentiatorFeature =
        features.get(SUBSCRIPTION_CATALOG_DIFFERENTIATOR_FEATURE)?.configuration ?? {};

    const [rootStore] = useStores([RootStore]);

    const isConsumerSubsSubscriber =
        (consumerSubscriptionPlan &&
            !consumerSubscriptionPlan.isLoading &&
            consumerSubscriptionPlan?.isSubscriber) ??
        false;

    const hydratePersonalPlanInfo = (bundle?: CertificationBundle) => {
        if (!consumerSubscriptionPlan?.isLoading && !!bundle) {
            const certificationBundleCourses = bundle.contents.map((course) => {
                return {
                    ...course,
                    is_in_personal_plan_collection: (
                        consumerSubscriptionPlan as ConsumerSubscriptionPlan
                    )?.contentCollectionIds?.some((subsCollectionId: string) =>
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (course as unknown as any).content_collections?.some(
                            (courseCollection: {id: number}) =>
                                // String conversion to make this robust to ids that are both strings and numbers
                                courseCollection.id.toString() === subsCollectionId.toString(),
                        ),
                    ),
                };
            });
            bundle.contents = certificationBundleCourses;
            return bundle;
        } else {
            return bundle;
        }
    };

    const topicProps = {
        ...props,
        ...experimentProps,
        presetValue: props.presetValue || TOPIC_PRESET_VALUES.ALL,
        relatedPages: props.relatedPages ?? [],
        isConsumerSubsSubscriber,
        isSubsLoading: consumerSubscriptionPlan?.isLoading ?? true,
        enableAssessmentsInPersonalPlan: rootStore.headerData.user?.enableAssessmentsInPersonalPlan,
        enableLabsInPersonalPlan: rootStore.headerData.user?.enableLabsInPersonalPlan,
        certification: props.certification,
        certificationBundle: hydratePersonalPlanInfo(props.certificationBundle),
    };

    const browseCourseContextProps = {
        browseBadgeHandlerOptions: {
            ...getBrowseCourseBadgesContextOptionsByExperiment(
                subscriptionCatalogDifferentiatorFeature,
            ),
            ...(showThreeCardVerticalLayout && {
                numberOfBadgesToShow: 2,
                priority: {[COURSE_BADGE_PREMIUM]: Number.MAX_SAFE_INTEGER},
            }),
        },
    };

    return (
        <>
            <BrowseCourseBadgesContext.Provider value={browseCourseContextProps}>
                {props.presetValue === 'free' ? (
                    <FreeTopic {...topicProps} />
                ) : (
                    <AsyncPriceCourseCardContext.Provider
                        value={
                            showThreeCardVerticalLayout
                                ? {
                                      useCourseProductCard: true,
                                      hideCourseDescription: true,
                                  }
                                : {}
                        }
                    >
                        <Topic {...topicProps} />
                    </AsyncPriceCourseCardContext.Provider>
                )}
            </BrowseCourseBadgesContext.Provider>
        </>
    );
});
